import { useState } from 'react';

import { DocumentFileUpload } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useFetchUserProfileInfoQuery } from '@backrs_ui/grow-ui/src/redux/profile/profile';

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { Box, Avatar, Typography, SxProps, Theme } from '@mui/material';

import { ImageUploadDialog } from './ImageUploadDialog';
import { ProfileFieldConfig } from '../types/profile.types';

const STRINGS = appStrings.modules.profile.edit;

type ProfileImageFieldProps = Readonly<{
  field: ProfileFieldConfig;
}>;

export const ProfileImageField = ({ field }: ProfileImageFieldProps) => {
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const { data: profile, refetch } = useFetchUserProfileInfoQuery(['publicProfile']);

  const [bannerImage, setBannerImage] = useState<string | null>(null);

  const profileImage = profile?.profile?.publicProfile?.profile_image;
  const profileBanner = profile?.profile?.publicProfile.profile_banner_image;
  const isFieldProfileImage = field.key === 'profileImage';
  const imageToUse = isFieldProfileImage ? profileImage : profileBanner;

  const handleOpenUploadDialog = () => {
    setIsUploadDialogOpen(true);
  };

  const handleFileSelect = (file: File) => {
    if (bannerImage) {
      URL.revokeObjectURL(bannerImage);
    }

    const newBannerImage = URL.createObjectURL(file);

    setBannerImage(newBannerImage);
    setIsUploadDialogOpen(true);
  };

  const handleCloseUploadDialog = () => {
    refetch(); // get the new images
    setIsUploadDialogOpen(false);

    if (bannerImage) {
      URL.revokeObjectURL(bannerImage);
      setBannerImage(null);
    }
  };

  return (
    <Box sx={avatarActionsSx}>
      {/* PROFILE HEADSHOT */}
      {isFieldProfileImage && imageToUse && (
        <Avatar src={imageToUse} sx={avatarSx}>
          <AccountCircleIcon />
        </Avatar>
      )}

      {/* PROFILE BANNER */}
      {!isFieldProfileImage && imageToUse && <Box component="img" src={imageToUse} sx={bannerSx} />}

      {/* UPLOADER WHEN NO IMAGE PRESENT */}
      {!imageToUse ? (
        <DocumentFileUpload
          onFileSelect={handleFileSelect}
          acceptedFileTypes={['.png', '.jpeg', '.jpg']}
          additionalText={null}
          inputId={isFieldProfileImage ? 'profile-image-upload' : 'profile-banner-upload'}
        />
      ) : (
        // UPLOADER WHEN IMAGE IS PRESENT
        <Typography
          sx={linkSx}
          variant="text-sm-semibold"
          color={({ backrs }) => backrs.colors['Colors/Text/text-brand-secondary']}
          onClick={handleOpenUploadDialog}
        >
          {STRINGS.update}
        </Typography>
      )}

      <ImageUploadDialog
        open={isUploadDialogOpen}
        onClose={handleCloseUploadDialog}
        type={isFieldProfileImage ? 'profile_media_id' : 'profile_banner_media_id'}
        preLoadedImage={bannerImage}
      />
    </Box>
  );
};

const avatarSx: SxProps<Theme> = {
  width: 72,
  height: 72,
};

const avatarActionsSx: SxProps<Theme> = {
  display: 'flex',
  gap: 2,
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
};

const linkSx: SxProps<Theme> = {
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
};

const bannerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '80%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: backrs.radius['radius-lg'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
});
